import { useEffect, useRef, useState } from "react";
import { Bell, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useIsAuthenticated } from "@/hooks/use-auth";

const DISMISS_KEY = "push-banner-dismissed";

function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

/**
 * A tap target for enabling push notifications.
 *
 * iOS only shows the notification permission prompt when requestPermission is
 * called from a user gesture. The automatic registration (on load / after
 * login) is not a gesture, so on iPhones it silently did nothing — devices
 * never registered. This banner's tap IS the gesture.
 *
 * The firebase module is preloaded on mount so the tap handler reaches
 * requestPermission without a long async gap — iOS revokes the gesture's
 * "transient activation" if too much time passes before the prompt.
 *
 * Shown only when logged in, push is supported, and permission is still
 * "default" (never asked). On iOS Safari outside the installed PWA the
 * Notification API doesn't exist, so the banner stays hidden and the existing
 * InstallPrompt nudges the user to install first.
 */
export function EnablePushBanner() {
  const { t } = useTranslation();
  const isAuthenticated = useIsAuthenticated();
  const [permission, setPermission] = useState<NotificationPermission | null>(
    () => (pushSupported() ? Notification.permission : null),
  );
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "1",
  );
  const [busy, setBusy] = useState(false);
  const registerRef = useRef<null | (() => Promise<void>)>(null);

  const visible = isAuthenticated && permission === "default" && !dismissed;

  useEffect(() => {
    if (!visible) return;
    import("@/lib/firebase-messaging").then((m) => {
      registerRef.current = m.registerPushNotifications;
    });
  }, [visible]);

  if (!visible) return null;

  const enable = async () => {
    setBusy(true);
    try {
      const register =
        registerRef.current ??
        (await import("@/lib/firebase-messaging")).registerPushNotifications;
      await register();
    } finally {
      setPermission(Notification.permission);
      setBusy(false);
    }
  };

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      className="fixed bottom-4 inset-x-4 z-40 md:max-w-md md:mx-auto"
      data-testid="enable-push-banner"
    >
      <div className="flex items-center gap-3 rounded-xl border bg-card text-card-foreground shadow-lg p-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Bell className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">
            {t("notifications.enableBannerTitle")}
          </p>
          <p className="text-xs text-muted-foreground leading-snug">
            {t("notifications.enableBannerBody")}
          </p>
        </div>
        <Button
          size="sm"
          onClick={enable}
          disabled={busy}
          data-testid="button-enable-push"
        >
          {t("notifications.enableBannerCta")}
        </Button>
        <button
          onClick={dismiss}
          className="shrink-0 p-1 text-muted-foreground hover:text-foreground"
          aria-label={t("common.close", "Close")}
          data-testid="button-dismiss-push-banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
